import React from 'react';
import { T, serif, fmt } from './ui';

// ════════════════════════════════════════════════════════════════════
// Graficos en SVG puro
//
// Sin libreria de graficos a proposito: recharts pesa unos 500 KB y
// los tecnicos abren la plataforma desde obra, muchas veces con señal
// pobre. Estos componentes ocupan unos pocos KB y hacen lo que hace
// falta.
// ════════════════════════════════════════════════════════════════════

/** Barras horizontales, que es lo que mejor funciona en pantalla de teléfono. */
export function BarrasHorizontales({ datos, moneda = 'PEN', maxFilas = 12, alTocar }) {
  if (!datos || datos.length === 0) {
    return <VacioGrafico />;
  }

  const visibles = datos.slice(0, maxFilas);
  const mayor = Math.max(...visibles.map((d) => d.valor), 1);

  return (
    <div style={{ display: 'grid', gap: 11 }}>
      {visibles.map((d, i) => (
        <div
          key={d.clave ?? i}
          onClick={alTocar ? () => alTocar(d) : undefined}
          style={{ cursor: alTocar ? 'pointer' : 'default' }}
        >
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            gap: 10, marginBottom: 4, alignItems: 'baseline',
          }}>
            <span style={{ fontSize: 13, color: T.text, minWidth: 0 }}>
              {d.etiqueta}
              {d.detalle && (
                <span style={{ color: T.muted, fontSize: 11 }}> · {d.detalle}</span>
              )}
            </span>
            <span style={{
              fontSize: 13, fontWeight: 600, color: T.navy, whiteSpace: 'nowrap',
            }}>
              {fmt.money(d.valor, moneda)}
            </span>
          </div>

          <div style={{
            height: 8, background: T.warm, borderRadius: 4, overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.max((d.valor / mayor) * 100, 1.5)}%`,
              height: '100%',
              background: d.color || T.navy,
              borderRadius: 4,
            }} />
          </div>

          {d.participacion != null && (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
              {d.participacion.toFixed(1)}% del total
            </div>
          )}
        </div>
      ))}

      {datos.length > maxFilas && (
        <div style={{ fontSize: 11, color: T.muted, textAlign: 'center', paddingTop: 4 }}>
          y {datos.length - maxFilas} más
        </div>
      )}
    </div>
  );
}

/**
 * Pareto: barras de mayor a menor mas la linea de porcentaje
 * acumulado. La franja resalta los conceptos que llegan al 80%.
 */
export function Pareto({ conceptos, vitales, moneda = 'PEN' }) {
  if (!conceptos || conceptos.length === 0) return <VacioGrafico />;

  const visibles = conceptos.slice(0, 10);
  const mayor = Math.max(...visibles.map((c) => c.total), 1);

  const W = 320;
  const H = 130;
  const anchoCol = W / visibles.length;

  // Puntos de la linea acumulada, en el centro de cada barra
  const puntos = visibles.map((c, i) => {
    const x = anchoCol * i + anchoCol / 2;
    const y = H - (c.acumulado / 100) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const y80 = H - 0.8 * H;

  return (
    <div>
      <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
        <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%"
             style={{ minWidth: 300, display: 'block' }}
             preserveAspectRatio="none">
          {/* Linea del 80% */}
          <line x1="0" y1={y80} x2={W} y2={y80}
                stroke={T.brass} strokeWidth="1" strokeDasharray="4 3" opacity="0.8" />
          <text x="2" y={y80 - 3} fontSize="8" fill={T.brass}>80%</text>

          {/* Barras */}
          {visibles.map((c, i) => {
            const alto = Math.max((c.total / mayor) * H, 2);
            const esVital = i < vitales;
            return (
              <rect
                key={c.cuenta ?? i}
                x={anchoCol * i + anchoCol * 0.15}
                y={H - alto}
                width={anchoCol * 0.7}
                height={alto}
                fill={esVital ? T.navy : T.border}
                rx="2"
              />
            );
          })}

          {/* Linea acumulada */}
          <polyline points={puntos} fill="none" stroke={T.brass} strokeWidth="2" />
          {visibles.map((c, i) => (
            <circle
              key={`p${c.cuenta ?? i}`}
              cx={anchoCol * i + anchoCol / 2}
              cy={H - (c.acumulado / 100) * H}
              r="2.5" fill={T.brass}
            />
          ))}

          {/* Numeros bajo cada barra */}
          {visibles.map((c, i) => (
            <text
              key={`t${c.cuenta ?? i}`}
              x={anchoCol * i + anchoCol / 2}
              y={H + 12}
              fontSize="8" fill={T.muted} textAnchor="middle"
            >
              {i + 1}
            </text>
          ))}
        </svg>
      </div>

      {/* Leyenda: la lista numerada que corresponde a las barras */}
      <div style={{ display: 'grid', gap: 7, marginTop: 12 }}>
        {visibles.map((c, i) => {
          const esVital = i < vitales;
          return (
            <div key={c.cuenta ?? i} style={{
              display: 'flex', gap: 9, alignItems: 'baseline',
              opacity: esVital ? 1 : 0.6,
            }}>
              <span style={{
                minWidth: 18, height: 18, borderRadius: 4,
                background: esVital ? T.navy : T.border,
                color: esVital ? '#fff' : T.muted,
                fontSize: 10, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {i + 1}
              </span>
              <span style={{ flex: 1, fontSize: 13, minWidth: 0 }}>
                {c.nombre}
                <span style={{ color: T.muted, fontSize: 11 }}> · {c.cantidad} gasto(s)</span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                {fmt.money(c.total, moneda)}
              </span>
              <span style={{ fontSize: 11, color: T.muted, minWidth: 38, textAlign: 'right' }}>
                {c.acumulado.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Linea de evolucion mensual. */
export function Tendencia({ serie, moneda = 'PEN' }) {
  if (!serie || serie.length === 0) return <VacioGrafico />;

  const W = 320;
  const H = 90;
  const mayor = Math.max(...serie.map((s) => s.total), 1);
  const paso = serie.length > 1 ? W / (serie.length - 1) : W;

  const puntos = serie.map((s, i) => {
    const x = paso * i;
    const y = H - (s.total / mayor) * (H - 8) - 4;
    return { x, y, ...s };
  });

  const linea = puntos.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `0,${H} ${linea} ${W},${H}`;

  const mesCorto = (m) => {
    const [, mm] = m.split('-');
    return ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
            'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'][Number(mm) - 1] || m;
  };

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}
           preserveAspectRatio="none">
        <polygon points={area} fill={T.navy} opacity="0.08" />
        <polyline points={linea} fill="none" stroke={T.navy} strokeWidth="2" />
        {puntos.map((p) => (
          <circle key={p.mes} cx={p.x} cy={p.y} r="3" fill={T.navy} />
        ))}
      </svg>

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: 6, fontSize: 10, color: T.muted,
      }}>
        {serie.map((s) => (
          <div key={s.mes} style={{ textAlign: 'center', flex: 1 }}>
            <div>{mesCorto(s.mes)}</div>
            <div style={{ color: T.text, fontSize: 11, fontWeight: 600, marginTop: 2 }}>
              {s.total >= 1000 ? `${(s.total / 1000).toFixed(1)}k` : s.total.toFixed(0)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Dona simple para repartos de dos o tres partes. */
export function Reparto({ partes, moneda = 'PEN' }) {
  const total = partes.reduce((s, p) => s + p.valor, 0);
  if (total <= 0) return <VacioGrafico />;

  let acumulado = 0;
  const radio = 34;
  const circunferencia = 2 * Math.PI * radio;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <svg width="88" height="88" viewBox="0 0 88 88" style={{ flexShrink: 0 }}>
        <g transform="rotate(-90 44 44)">
          {partes.map((p, i) => {
            const fraccion = p.valor / total;
            const trazo = fraccion * circunferencia;
            const offset = -acumulado * circunferencia;
            acumulado += fraccion;
            return (
              <circle
                key={i} cx="44" cy="44" r={radio}
                fill="none" stroke={p.color} strokeWidth="16"
                strokeDasharray={`${trazo} ${circunferencia - trazo}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </g>
      </svg>

      <div style={{ display: 'grid', gap: 7, flex: 1, minWidth: 130 }}>
        {partes.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: 3,
              background: p.color, flexShrink: 0,
            }} />
            <span style={{ fontSize: 12, flex: 1 }}>{p.etiqueta}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
              {((p.valor / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VacioGrafico() {
  return (
    <div style={{
      padding: '26px 16px', textAlign: 'center',
      color: T.muted, fontSize: 13, background: T.warm, borderRadius: 8,
    }}>
      Sin datos en el periodo elegido.
    </div>
  );
}

/** Cifra grande para encabezar un bloque. */
export function Cifra({ etiqueta, valor, detalle, color }) {
  return (
    <div>
      <div style={{
        fontSize: 10, color: T.muted, letterSpacing: '.05em',
        textTransform: 'uppercase', fontWeight: 600,
      }}>
        {etiqueta}
      </div>
      <div style={{
        fontFamily: serif, fontSize: 26, fontWeight: 600,
        color: color || T.navy, margin: '3px 0 1px', lineHeight: 1.1,
      }}>
        {valor}
      </div>
      {detalle && (
        <div style={{ fontSize: 11, color: T.muted }}>{detalle}</div>
      )}
    </div>
  );
}
